import PropTypes from 'prop-types';

/**
 * Draws the blocks `composeReceipt` produces.
 *
 * The bill window drew them one way and the editor's preview drew a flat string
 * from the same source, which threw away everything but the words: a field set
 * to Bold or Big changed nothing on the preview beside the switch that set it,
 * so the switch looked broken. One renderer, so what a design does is visible
 * wherever the design is shown.
 */
const ALIGN = { left: 'text-left', center: 'text-center', right: 'text-right' };

export const ReceiptBlocks = ({ blocks, width, fontSize }) => (
  // Rows are already padded to the paper's character width by the composer, so
  // the whitespace carries the columns — hence `whitespace-pre` and a monospace
  // face. Anything else and nothing lines up with what the printer produces.
  <div
    className="whitespace-pre font-mono"
    style={fontSize ? { fontSize: `${fontSize}px`, lineHeight: 1.5 } : undefined}
  >
    {blocks.map((b, i) => {
      if (b.kind === 'blank') return <div key={i} style={{ height: '0.8em' }} />;
      if (b.kind === 'divider') {
        return <div key={i} className="text-left">{'-'.repeat(b.width || width)}</div>;
      }
      if (b.kind === 'qr' || b.kind === 'barcode') {
        // The thermal printer draws these itself from ESC/POS commands. On
        // screen there is no code to draw without a barcode library, and a
        // drawn-on rectangle would be a lie about what prints — so it says what
        // will be encoded, which a person can read even though a scanner
        // cannot.
        return (
          <div key={i} className="text-center my-2 whitespace-normal">
            <div className="border border-current border-dashed px-2 py-2 inline-block">
              <p className="font-bold" style={{ fontSize: '0.85em' }}>
                [ {b.kind === 'qr' ? 'QR CODE' : 'BARCODE'} ]
              </p>
              <p className="break-all mt-0.5" style={{ fontSize: '0.75em' }}>{b.data}</p>
            </div>
            {b.caption && <p className="mt-1" style={{ fontSize: '0.85em' }}>{b.caption}</p>}
          </div>
        );
      }
      return (
        <div
          key={i}
          className={`${ALIGN[b.align] || 'text-left'} ${b.bold ? 'font-bold' : ''}`}
          // Big is double-height on the printer, so it is drawn at double size
          // here too rather than as a fixed pixel figure — the preview shrinks
          // its type to fit the paper, and a fixed size would not shrink with it.
          style={b.big ? { fontSize: '1.6em', lineHeight: 1.2 } : undefined}
        >
          {b.text || ' '}
        </div>
      );
    })}
  </div>
);

ReceiptBlocks.propTypes = {
  blocks: PropTypes.array.isRequired,
  width: PropTypes.number,
  fontSize: PropTypes.number,
};
